import { Config } from "atoms/Rtool/newConfigAtoms";
import type { Powertrain } from "atoms/powertrainAtom";
import { throwIfNotOk } from "./apiError";
import type { CopyFailedItem } from "./submitRtool";

// 計算セット丸ごとコピー非同期化（issue #41/#42）のジョブ状態契約。
// job（ジョブ全体）の status は running を含む4値、task 単位は BE 訂正コメント
// （issue #41）により running を返さない3値のみ（実行中でも queued のまま見える）。
export type CopyJobStatus = "queued" | "running" | "completed" | "failed";
export type CopyJobTaskStatus = "queued" | "completed" | "failed";

export type CopyJobTaskSummary = {
    succeeded: number;
    failed: number;
    skipped: number;
};

export type CopyJobTask = {
    task: string;
    status: CopyJobTaskStatus;
    summary: CopyJobTaskSummary | null;
};

export type CopyJobFailedReason = "worker_lost" | "dlq" | "enqueue_failed";

export type CopyJobSnapshot = {
    job_id: string;
    status: CopyJobStatus;
    status_url: string;
    created_at: string;
    started_at: string | null;
    finished_at: string | null;
    progress: { total_tasks: number; done_tasks: number };
    summary: CopyJobTaskSummary;
    tasks: CopyJobTask[];
    message?: string;
    failed_items?: CopyFailedItem[];
    failed_reason?: CopyJobFailedReason;
    incomplete_tasks?: string[];
};

export const isTerminalCopyJobStatus = (status: CopyJobStatus): boolean =>
    status === "completed" || status === "failed";

// GET {status_url}（= .../datasets/copy/jobs/{job_id}）。status_url は202/GET応答が返す
// percent-encode済みパスなので、そのままベースURLへ連結する（FEが6IDから組み立てる場面は無い）。
export const fetchCopyJobStatus = async (
    authToken: string,
    statusUrl: string,
): Promise<CopyJobSnapshot> => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}${statusUrl}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    await throwIfNotOk(response, "fetchCopyJobStatus");
    return await response.json();
};

// GET .../datasets/copy/jobs/latest。submitRtool.ts の submitCalcScopedCopy と同じ
// encode規約でURLを組む（既存ファイルは変更しない方針のため、この短い組み立てをここに複製する）。
// 404は「このコピー先ではまだコピーしたことがない（または7日超過）」という正常な答えのため、
// throwせず null を返す。エラーとして扱うかどうかは呼び出し側が文脈で決める。
export const fetchLatestCopyJob = async (
    authToken: string,
    powertrain: Powertrain,
    config: Config,
): Promise<CopyJobSnapshot | null> => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/calcs/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/copy/jobs/latest`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    if (response.status === 404) return null;

    await throwIfNotOk(response, "fetchLatestCopyJob");
    return await response.json();
};
